import { FormGroup, FormHelperText } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import classNames from 'classnames';
import { FormikProps, useField } from 'formik';
import Fuse from 'fuse.js';
import { isEmpty, isNil } from 'lodash';
import Script from 'next/script';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { AUSTRALIAN_STATES, postcodeRegex } from 'src/constants';
import { getOr } from 'src/utils/getOr';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { FormikInput } from '../FormikInput/FormikInput';
import { InputSelectFormik } from '../Input/InputSelectFormik';
import {
  boldMatchCharacters,
  extractAddressFromGoogleInput,
  extractAddressFromManuallyInput,
  validateStreetLength,
} from './FormikInputAddressAutocomplete.helpers';
import styles from './FormikInputAddressAutocomplete.module.scss';
import { usePlacesService } from './usePlacesService.hooks';
import { InputFormik } from '../Input/InputFormik';

type InputAddressAutocompleteValidationConfig = {
  streetRequiredValidation?: string;
  streetTooLongValidation?: string;
  suburbRequiredValidation?: string;
  postcodeRequiredValidation?: string;
  postcodeFormatValidation?: string;
  stateValidationValidation?: string;
};

const maxCharacterValidation = (limit: number) => `Must be at most ${limit} characters.`;

export const getInputAddressAutocompleteValidation = ({
  streetRequiredValidation = 'Please add your delivery address',
  streetTooLongValidation = 'Please ensure your delivery address is properly formatted',
  suburbRequiredValidation = 'Please add your delivery suburb',
  postcodeRequiredValidation = 'Enter your postcode',
  postcodeFormatValidation = 'Please enter a valid postcode',
  stateValidationValidation = 'Enter your state',
}: InputAddressAutocompleteValidationConfig = {}) =>
  Yup.object({
    street: Yup.string()
      .required(streetRequiredValidation)
      .test('streetNameTooLong', streetTooLongValidation, validateStreetLength),
    apartment: Yup.string().max(20, maxCharacterValidation(20)),
    suburb: Yup.string()
      .required(suburbRequiredValidation)
      .max(30, maxCharacterValidation(30))
      .required(suburbRequiredValidation),
    postcode: Yup.string()
      .matches(postcodeRegex, postcodeFormatValidation)
      .max(20, maxCharacterValidation(20))
      .required(postcodeRequiredValidation),
    state: Yup.string()
      .oneOf(AUSTRALIAN_STATES)
      .max(20, maxCharacterValidation(20))
      .required(stateValidationValidation),
  });

export const OptionalInputAddressAutocompleteValidation = Yup.object({
  street: Yup.string(),
  apartment: Yup.string(),
  suburb: Yup.string(),
  postcode: Yup.string(),
  state: Yup.string(),
});

const defaultAddressValidation = getInputAddressAutocompleteValidation();

export type InputAddressAutocompleteAddress = Omit<
  ReturnType<typeof defaultAddressValidation.validateSync>,
  'state'
> & { state: any };

export const defaultValueInputAddressAutocomplete: Prettify<InputAddressAutocompleteAddress> = {
  street: '',
  apartment: '',
  suburb: '',
  postcode: '',
  state: '' as any,
};

type FormikInputAddressAutocompleteProps = {
  name: string;
  helperText?: ReactNode;
};

export const FormikInputAddressAutocomplete: FC<FormikInputAddressAutocompleteProps & FormikProps<any>> = ({
  name,
  helperText,
  setFieldValue,
  handleBlur,
  validateField,
  values,
}) => {
  const [_field, _meta, helpers] = useField(`${name}.street`);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isGoogleServiceReady, setGoogleServiceReady] = useState(false);
  const apartmentInputRef = useRef<any>(null);

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading, resetPlacePredictions } =
    usePlacesService({
      enable: isGoogleServiceReady,
    });

  const onPlaceSelect = (_e: any, newValue: google.maps.places.AutocompletePrediction | string | null) => {
    setIsPlaceSelected(true);
    setIsDropdownOpen(false);
    if (!newValue || typeof newValue === 'string') return;
    resetAddress();
    // Fetch place detail
    placesService?.getDetails({ placeId: newValue.place_id, fields: ['address_components'] }, (placeDetail) => {
      const { street, apartment, suburb, postcode, state } = extractAddressFromGoogleInput(
        placeDetail,
        newValue.description,
      );
      fillAddress({ street, apartment, suburb, postcode, state });
    });
  };

  const resetAddress = () => {
    setFieldValue(`${name}.apartment`, '');
    setFieldValue(`${name}.suburb`, '');
    setFieldValue(`${name}.postcode`, '');
    setFieldValue(`${name}.state`, undefined);
  };

  useEffect(() => {
    if (isPlaceSelected) revalidateField();
  }, [values[name]]);

  const revalidateField = () => {
    validateField(`${name}.suburb`);
    validateField(`${name}.postcode`);
    validateField(`${name}.state`);
    validateField(`${name}.street`);
  };

  const fillAddress = ({
    street,
    apartment,
    suburb,
    postcode,
    state,
  }: {
    apartment?: string;
    street?: string;
    suburb?: string;
    postcode?: string;
    state?: string;
  }) => {
    if (!isNil(street)) {
      setFieldValue(`${name}.street`, street, false);
    }
    if (!isNil(apartment)) {
      setFieldValue(`${name}.apartment`, apartment, false);
    }
    if (!isNil(suburb)) {
      setFieldValue(`${name}.suburb`, suburb, false);
    }
    if (!isNil(postcode)) {
      setFieldValue(`${name}.postcode`, postcode, false);
    }
    if (!isNil(state)) {
      setFieldValue(`${name}.state`, state, false);
    }
  };

  const onStreetInputBlur = (e: any) => {
    setIsDropdownOpen(false);
    if (isPlaceSelected) {
      setIsPlaceSelected(false);
    } else {
      const { street, apartment, suburb, postcode, state } = extractAddressFromManuallyInput(inputValue);
      fillAddress({ street, apartment, suburb, postcode, state });
    }
    handleBlur(e);
  };

  const onStreetInputChange = (_e: React.ChangeEvent<{}>, inputValue: string) => {
    if (inputValue) {
      getPlacePredictions({
        input: inputValue,
        componentRestrictions: { country: ['au'] },
        types: ['address'],
      });
      setIsDropdownOpen(true);
    } else {
      resetPlacePredictions();
      fillAddress({ street: '', apartment: '', suburb: '', postcode: '', state: '' });
      setIsDropdownOpen(false);
    }
    setInputValue(inputValue);
  };

  useEffect(() => {
    if ((window as any).google?.maps?.places) {
      setGoogleServiceReady(true);
    }
  }, []);

  const onOpenChatWidget = () => {
    if (typeof window !== 'undefined' && typeof window.LiveChatWidget !== 'undefined' && !!window.LiveChatWidget)
      window.LiveChatWidget.call('maximize');

    pushToDataLayer({
      event: `checkout_click`,
      value: `license_contact_carma`,
    });
  };

  const getOptionLabel = (option: google.maps.places.AutocompletePrediction | string) =>
    typeof option === 'string' ? option : option.description;

  const filterOptions = (
    options: google.maps.places.AutocompletePrediction[],
    { inputValue }: { inputValue: string },
  ) => {
    const collection = new Fuse(options, {
      keys: ['description'],
      useExtendedSearch: true,
    });
    const result = collection.search(inputValue);
    return result.map((option) => option.item);
  };

  const showHelperText = !!values[name]?.state && values[name]?.state !== 'NSW' && !isEmpty(helperText);

  return (
    <>
      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GG_MAP_KEY}&libraries=places`}
        onLoad={() => {
          setGoogleServiceReady(true);
        }}
        strategy="afterInteractive"
      />
      <FormGroup
        className={classNames(styles.formWrapper, {
          [styles.withHelperText]: showHelperText,
        })}
      >
        <div className={styles.autocompleteContainer}>
          <Autocomplete
            id="FormikInputAddressAutocomplete-autocomplete"
            data-testid="FormikInputAddressAutocomplete-search"
            fullWidth
            className={styles.root}
            options={placePredictions}
            disablePortal
            getOptionLabel={getOptionLabel}
            getOptionSelected={(option, value) => option.description === value.description}
            filterOptions={filterOptions}
            freeSolo
            open={isDropdownOpen}
            loading={isPlacePredictionsLoading}
            autoComplete
            selectOnFocus
            closeIcon={null}
            classes={{ popper: styles.popper }}
            autoHighlight
            onInputChange={onStreetInputChange}
            onChange={onPlaceSelect}
            onBlur={(e) => {
              onStreetInputBlur(e);
              helpers.setTouched(false);
            }}
            renderInput={({ InputProps: { startAdornment, endAdornment, ...InputProps }, ...params }) => (
              <FormikInput
                {...params}
                InputProps={{ ...InputProps, startAdornment: startAdornment as any, endAdornment: endAdornment as any }}
                inputProps={{ ...params.inputProps, value: getOr(values, `${name}.street`, '') }}
                id={`${name}.street`}
                autoComplete="street-address"
                type="text"
                name={`${name}.street`}
                data-testid="Street-Address"
                label="Street address"
                className={styles.locationInput}
              />
            )}
            renderOption={(option) => (
              <div className={styles.itemOption} data-testid="MakeModelAutocomplete-option" key={option.place_id}>
                <span className={styles.labelOption}>
                  {boldMatchCharacters(option.description, option.matched_substrings)}
                </span>
              </div>
            )}
          />
        </div>
        <FormikInput
          inputProps={{ ref: apartmentInputRef }}
          id={`${name}.apartment`}
          name={`${name}.apartment`}
          label="Apartment, suite, etc (optional)"
          type="text"
        />
        <div className={styles.row}>
          <InputFormik
            id={`${name}.suburb`}
            name={`${name}.suburb`}
            label="Suburb"
            type="text"
            autoComplete="address-level2"
            className={styles.suburb}
          />
          <FormikInput
            id={`${name}.postcode`}
            name={`${name}.postcode`}
            label="Postcode"
            type="text"
            autoComplete="postal-code"
            inputMode="numeric"
            inputProps={{ maxLength: 4 }}
            className={styles.postcode}
          />
          <InputSelectFormik
            id={`${name}.state`}
            name={`${name}.state`}
            autoComplete="address-level1"
            label="State"
            allowEmpty
            options={AUSTRALIAN_STATES.map((state) => ({ value: state, label: state }))}
            className={styles.state}
          />
        </div>
        {showHelperText && (
          <FormHelperText>
            <>
              {helperText}
              <span>{`If you have any questions, `}</span>
              <button type="button" className={styles.contactButton} onClick={onOpenChatWidget}>
                contact us.
              </button>
            </>
          </FormHelperText>
        )}
      </FormGroup>
    </>
  );
};
